import { Check, CheckCircleOutline, Clear, DoNotDisturbAlt, Refresh } from "@mui/icons-material";
import type React from "react";
import { CommonButton } from "../../ui/commonButton";
import type { DiffResult } from "../utils/diffUtils";
import styles from "./styles.module.scss";

// HunkData interface used across components
export interface HunkData {
  originalOriginal: string;
  originalUpdated: string;
  targetOriginal: string;
  targetUpdated: string;
}

interface UnifiedDiffViewProps {
  diffResult: DiffResult;
  showLineNumbers: boolean;
  compact?: boolean;
  expandedHunks: Set<number>;
  hunkSelections: Map<number, "original" | "updated">;
  onHunkSelection: (hunkIndex: number, selection: "original" | "updated") => void;
  onHunkReset: (hunkIndex: number) => void;
  editingHunk: number | null;
  editedContent: string;
  setEditedContent: (content: string) => void;
  onStartEdit: (hunkIndex: number) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  hunkData: Map<number, HunkData>;
  renderEditOverlay: (hunkIndex: number, hasChanges: boolean) => React.ReactNode;
  activeDisplayIndex: number | null;
  onCardActivate: (displayIndex: number) => void;
  hunkDisplayOrder: Map<number, number>;
  registerTextareaRef: (hunkIndex: number, element: HTMLTextAreaElement | null) => void;
}

export const UnifiedDiffView: React.FC<UnifiedDiffViewProps> = ({
  diffResult,
  showLineNumbers,
  compact = false,
  expandedHunks,
  hunkSelections,
  onHunkSelection,
  onHunkReset,
  editingHunk,
  editedContent,
  setEditedContent,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  hunkData,
  renderEditOverlay,
  activeDisplayIndex,
  onCardActivate,
  hunkDisplayOrder,
  registerTextareaRef,
}) => {
  return (
    <div className={styles["unifiedView"]}>
      {diffResult.hunks.map((hunk, hunkIndex) => {
        const hasChanges = hunk.lines.some((l) => l.type !== "context");
        if (!hasChanges) {
          return null;
        }
        const isExpanded = !compact || expandedHunks.has(hunkIndex);
        const changeCount = hunk.lines.filter((l) => l.type !== "context").length;
        const selection = hunkSelections.get(hunkIndex);
        const isApproved = selection === "updated";
        const canEdit = hasChanges && selection === "updated";
        const displayIndex = hunkDisplayOrder.get(hunkIndex) ?? hunkIndex;

        const diffLineNodes = hunk.lines
          .flatMap((line, lineIndex) => {
            let isLineActive = true;

            if (selection) {
              isLineActive =
                (selection === "original" && line.type !== "insert") ||
                (selection === "updated" && line.type !== "delete");
            }

            const currentHunkData = hunkData.get(hunkIndex);

            if (currentHunkData) {
              if (line.type === "delete") {
                if (lineIndex === hunk.lines.findIndex((l) => l.type === "delete")) {
                  const targetLines = currentHunkData.targetOriginal.split("\n");
                  return targetLines.map((targetLine: string, targetLineIndex: number) => (
                    <div
                      key={`${lineIndex}-target-${targetLineIndex}`}
                      className={`${styles["line"]} ${styles["delete"]} ${!isLineActive ? styles["inactive"] : ""}`}
                    >
                      {showLineNumbers && (
                        <div className={styles["lineNumbers"]}>
                          <span className={styles["oldLineNumber"]}>
                            {line.lineNumber?.old || ""}
                          </span>
                          <span className={styles["newLineNumber"]}></span>
                        </div>
                      )}
                      <div className={styles["marker"]}>-</div>
                      <div className={styles["content"]}>
                        <pre>{targetLine}</pre>
                      </div>
                    </div>
                  ));
                }
                return null;
              }

              if (line.type === "insert") {
                if (lineIndex === hunk.lines.findIndex((l) => l.type === "insert")) {
                  const targetLines = currentHunkData.targetUpdated.split("\n");
                  return targetLines.map((targetLine: string, targetLineIndex: number) => (
                    <div
                      key={`${lineIndex}-target-${targetLineIndex}`}
                      className={`${styles["line"]} ${styles["insert"]} ${!isLineActive ? styles["inactive"] : ""}`}
                    >
                      {showLineNumbers && (
                        <div className={styles["lineNumbers"]}>
                          <span className={styles["oldLineNumber"]}></span>
                          <span className={styles["newLineNumber"]}>
                            {line.lineNumber?.new || ""}
                          </span>
                        </div>
                      )}
                      <div className={styles["marker"]}>+</div>
                      <div className={styles["content"]}>
                        <pre>{targetLine}</pre>
                      </div>
                    </div>
                  ));
                }
                return null;
              }
            }

            return (
              <div
                key={lineIndex}
                className={`${styles["line"]} ${styles[line.type]} ${!isLineActive ? styles["inactive"] : ""}`}
              >
                {showLineNumbers && (
                  <div className={styles["lineNumbers"]}>
                    <span className={styles["oldLineNumber"]}>{line.lineNumber?.old || ""}</span>
                    <span className={styles["newLineNumber"]}>{line.lineNumber?.new || ""}</span>
                  </div>
                )}
                <div className={styles["marker"]}>{line.marker}</div>
                <div className={styles["content"]}>
                  <pre>{line.content}</pre>
                </div>
              </div>
            );
          })
          .filter(Boolean) as React.ReactNode[];

        const diffArea = (
          <div
            className={`${styles["diffAreaWrapper"]} ${canEdit ? styles["editable"] : hasChanges ? styles["notEditable"] : ""}`}
            onClick={() => canEdit && onStartEdit(hunkIndex)}
          >
            {renderEditOverlay(hunkIndex, hasChanges)}
            <div className={styles["diffArea"]}>{diffLineNodes}</div>
          </div>
        );
        return (
          <div key={hunkIndex} className={styles["hunk"]}>
            {isExpanded && (
              <div
                className={`${styles["hunkContainer"]} ${activeDisplayIndex === displayIndex ? styles["diffCardActive"] : ""}`}
                onFocus={() => onCardActivate(displayIndex)}
                onClick={() => onCardActivate(displayIndex)}
              >
                <div className={styles["diffCardHeader"]}>
                  <div className={styles["diffCardHeaderLeft"]}>
                    <span className={styles["diffCardBadge"]}>差分 {displayIndex + 1}</span>
                    <span className={styles["diffCardInfo"]}>
                      {hasChanges ? `変更行 ${changeCount}` : "変更はありません"}
                    </span>
                  </div>
                  {hasChanges && (
                    <div className={styles["diffCardActions"]}>
                      {selection ? (
                        <>
                          <div
                            className={`${styles["selectionStatus"]} ${isApproved ? styles["statusApproved"] : styles["statusRejected"]}`}
                          >
                            {isApproved ? (
                              <>
                                <Check sx={{ fontSize: "1rem" }} />
                                <span className={styles["selectionStatusLabel"]}>承認済み</span>
                              </>
                            ) : (
                              <>
                                <Clear sx={{ fontSize: "1rem" }} />
                                <span className={styles["selectionStatusLabel"]}>拒否済み</span>
                              </>
                            )}
                          </div>
                          <CommonButton
                            variant="diff-edit"
                            onClick={() => onHunkReset(hunkIndex)}
                            icon={<Refresh sx={{ fontSize: "1rem" }} />}
                            disabled={editingHunk === hunkIndex}
                          >
                            リセット
                          </CommonButton>
                        </>
                      ) : (
                        <>
                          <CommonButton
                            variant="diff-reject"
                            onClick={() => onHunkSelection(hunkIndex, "original")}
                            icon={<DoNotDisturbAlt sx={{ fontSize: "1rem" }} />}
                            disabled={editingHunk === hunkIndex}
                          >
                            拒否
                          </CommonButton>
                          <CommonButton
                            variant="diff-approve"
                            onClick={() => onHunkSelection(hunkIndex, "updated")}
                            icon={
                              <CheckCircleOutline sx={{ fontSize: "1rem", color: "#1A8FC1" }} />
                            }
                            disabled={editingHunk === hunkIndex}
                          >
                            承認
                          </CommonButton>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className={styles["diffCardContent"]}>
                  {editingHunk === hunkIndex ? (
                    <div className={styles["editMode"]}>
                      <textarea
                        ref={(element) => registerTextareaRef(hunkIndex, element)}
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className={styles["editTextarea"]}
                        placeholder="編集内容を入力してください。"
                      />
                      <div className={styles["editModeButtons"]}>
                        <CommonButton variant="diff-reject" onClick={onCancelEdit}>
                          キャンセル
                        </CommonButton>
                        <CommonButton variant="diff-approve" onClick={onSaveEdit}>
                          保存
                        </CommonButton>
                      </div>
                    </div>
                  ) : (
                    diffArea
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
